/**
 * Analytics — trend + performance intelligence, built entirely on the
 * Business Intelligence Engine.
 *
 * This page NEVER queries raw orders/products/customers. Every number
 * on this screen comes from /api/business-intelligence/*.
 *
 * Sections
 *  1. Filter bar (date range, marketplace, category)
 *  2. Headline KPIs (revenue, orders, AOV, units) with period benchmarks
 *  3. Revenue trend chart (day/week/month/quarter grain switch)
 *  4. Growth benchmarks (yesterday / last week / month / quarter / year)
 *  5. Day-of-week heatmap + peak days
 *  6. Marketplace performance (bar + table)
 *  7. Category performance (bar + table)
 *  8. Top product performance (top 10 by revenue)
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Legend,
} from "recharts";
import {
  TrendingUp, TrendingDown, ArrowUp, ArrowDown, Minus, Calendar, Store, Boxes,
  Package, Sparkles,
} from "lucide-react";
import { api } from "../lib/api";
import { formatINR as money, formatINRShort as moneyShort, formatNumber as num, formatPct } from "../lib/format";
import GlobalFilterBar, { useFilters } from "../components/dashboard/GlobalFilterBar";
import ModuleEmptyState from "../components/app/ModuleEmptyState";

const GRAINS = [
  { key: "day", label: "Daily", days_back: 90 },
  { key: "week", label: "Weekly", days_back: 180 },
  { key: "month", label: "Monthly", days_back: 365 },
  { key: "quarter", label: "Quarterly", days_back: 365 * 2 },
];

const TREND_META = {
  rapid_growth:  { color: "text-emerald-400", Icon: TrendingUp, label: "Rapid growth" },
  growing:       { color: "text-emerald-400", Icon: ArrowUp,    label: "Growing" },
  stable:        { color: "text-zinc-400",    Icon: Minus,      label: "Stable" },
  declining:     { color: "text-amber-400",   Icon: ArrowDown,  label: "Declining" },
  rapid_decline: { color: "text-red-400",     Icon: TrendingDown, label: "Rapid decline" },
  unknown:       { color: "text-zinc-500",    Icon: Minus,      label: "—" },
};

const pct = (n) => formatPct(n, { asFraction: false, digits: 1 });

// ── Small helpers ────────────────────────────────────────────────────────

function KPI({ label, value, sub, testid }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-5" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">{label}</p>
      <p className="mt-3 text-2xl font-semibold tracking-tight text-zinc-50 tabular-nums" style={{ fontFamily: "var(--font-heading)" }}>{value}</p>
      {sub && <p className="mt-1 text-xs text-zinc-500">{sub}</p>}
    </div>
  );
}

function BenchmarkRow({ label, payload, testid }) {
  if (!payload) return null;
  const meta = TREND_META[payload.trend] || TREND_META.unknown;
  const Icon = meta.Icon;
  return (
    <div className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-zinc-800/40" data-testid={testid}>
      <span className="text-sm text-zinc-300">{label}</span>
      <div className="flex items-center gap-4">
        <span className="text-sm text-zinc-100 tabular-nums">{money(payload.current)}</span>
        <span className={`text-xs ${meta.color} flex items-center gap-1 min-w-[80px] justify-end`}>
          <Icon className="h-3 w-3" />
          {pct(payload.change_pct)}
        </span>
      </div>
    </div>
  );
}

// Custom Recharts tooltip so we stay on-brand + use ₹.
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-zinc-700 bg-zinc-900/95 backdrop-blur px-3 py-2 shadow-xl">
      <p className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">{label}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 text-xs">
          <span className="w-2 h-2 rounded-sm" style={{ background: p.color }} />
          <span className="text-zinc-400">{p.name}</span>
          <span className="ml-auto text-zinc-100 tabular-nums">
            {p.dataKey === "revenue" ? money(p.value) : num(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────

export default function Analytics() {
  const { state: filters, setState: setFilters, query } = useFilters({ range: "90d" });
  const [grain, setGrain] = useState("day");

  const [kpis, setKpis] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [trends, setTrends] = useState(null);
  const [products, setProducts] = useState(null);
  const [categories, setCategories] = useState(null);
  const [marketplaces, setMarketplaces] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const qp = query ? `?${query}` : "";
  const activeGrain = useMemo(() => GRAINS.find((g) => g.key === grain) || GRAINS[0], [grain]);

  useEffect(() => {
    (async () => {
      setRefreshing(true);
      try {
        const [k, g, t, p, c, m] = await Promise.all([
          api.get(`/business-intelligence/kpis${qp}`),
          api.get(`/business-intelligence/growth${qp}`),
          api.get(`/business-intelligence/trends${qp ? qp + "&" : "?"}grain=${activeGrain.key}&days_back=${activeGrain.days_back}`),
          api.get(`/business-intelligence/products${qp}`),
          api.get(`/business-intelligence/categories${qp}`),
          api.get(`/business-intelligence/marketplaces${qp}`),
        ]);
        setKpis(k.data); setGrowth(g.data); setTrends(t.data);
        setProducts(p.data); setCategories(c.data); setMarketplaces(m.data);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    })();
  }, [qp, activeGrain.key, activeGrain.days_back]);

  const marketplaceOptions = useMemo(
    () => (marketplaces?.rows || []).map((r) => r.marketplace).filter(Boolean),
    [marketplaces]
  );
  const categoryOptions = useMemo(
    () => (categories?.rows || []).map((r) => r.category).filter(Boolean),
    [categories]
  );

  if (loading) {
    return <div className="text-sm text-zinc-500" data-testid="analytics-loading">Loading analytics…</div>;
  }
  if (!kpis || kpis.orders === 0) {
    return (
      <div className="max-w-7xl mx-auto" data-testid="analytics-page-empty">
        <ModuleEmptyState
          icon={Sparkles}
          title="Import your first commerce report to unlock analytics"
          description="Milkath analytics blends trends, marketplace, category and product performance into one live view — powered entirely by the Business Intelligence Engine."
          testid="analytics-empty"
        />
      </div>
    );
  }

  const series = trends?.series?.series || [];
  const dow = trends?.day_of_week || [];
  const peakDays = trends?.peak_days || [];

  // Marketplace & category — pick top 8 by revenue for chart legibility
  const mpRows = (marketplaces?.rows || []).slice(0, 8);
  const catRows = (categories?.rows || []).slice(0, 8);
  const topProducts = (products?.rows || []).slice(0, 10);

  return (
    <div className="max-w-7xl mx-auto space-y-10" data-testid="analytics-page">
      <GlobalFilterBar
        state={filters} setState={setFilters}
        marketplaces={marketplaceOptions} categories={categoryOptions}
        onOpenSearch={() => {}}
      />

      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500/80 mb-2">Analytics</p>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50" style={{ fontFamily: "var(--font-heading)" }}>
          Trends &amp; performance
        </h1>
        <p className="mt-2 text-zinc-400 max-w-2xl">
          Revenue trends, growth benchmarks, and cross-channel performance — computed by the Milkath BI Engine.
          {refreshing && <span className="ml-2 text-zinc-600 text-xs">· refreshing…</span>}
        </p>
      </header>

      {/* Headline KPIs */}
      <section>
        <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 mb-4">Headline metrics</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Gross revenue" value={money(kpis.gross_revenue)} testid="an-kpi-revenue" />
          <KPI label="Orders" value={num(kpis.orders)} testid="an-kpi-orders" />
          <KPI label="Avg order value" value={money(kpis.aov)} testid="an-kpi-aov" />
          <KPI label="Units sold" value={num(kpis.units_sold)} sub={`${kpis.avg_units_per_order.toFixed(2)} per order`} testid="an-kpi-units" />
        </div>
      </section>

      {/* Revenue Trend Chart */}
      <section data-testid="an-trend-section">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 flex items-center gap-2">
            <Calendar className="h-3.5 w-3.5" /> Revenue trend
          </h2>
          <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-800 rounded-lg p-1">
            {GRAINS.map((g) => (
              <button
                key={g.key}
                onClick={() => setGrain(g.key)}
                data-testid={`an-grain-${g.key}`}
                className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                  grain === g.key ? "bg-zinc-800 text-zinc-100" : "text-zinc-500 hover:text-zinc-300"
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4 h-[320px]">
          {series.length === 0 ? (
            <p className="text-sm text-zinc-500 h-full flex items-center justify-center">No data in this range.</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="period" stroke="#71717a" fontSize={11} />
                <YAxis stroke="#71717a" fontSize={11} tickFormatter={(v) => moneyShort(v)} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11, color: "#a1a1aa" }} />
                <Line type="monotone" dataKey="revenue" stroke="#f97316" strokeWidth={2} dot={false} name="Revenue" />
                <Line type="monotone" dataKey="orders" stroke="#a1a1aa" strokeWidth={1.5} dot={false} name="Orders" yAxisId={0} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>

      {/* Growth benchmarks + Peak days */}
      <section className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid="an-growth">
          <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 mb-3">Growth benchmarks</h3>
          <BenchmarkRow label="Yesterday vs day before" payload={growth?.benchmarks?.yesterday?.revenue} testid="an-bench-yesterday" />
          <BenchmarkRow label="This week vs last week" payload={growth?.benchmarks?.last_week?.revenue} testid="an-bench-week" />
          <BenchmarkRow label="This month vs last month" payload={growth?.benchmarks?.last_month?.revenue} testid="an-bench-month" />
          <BenchmarkRow label="This quarter vs last quarter" payload={growth?.benchmarks?.last_quarter?.revenue} testid="an-bench-quarter" />
          <BenchmarkRow label="This year vs last year" payload={growth?.benchmarks?.last_year?.revenue} testid="an-bench-year" />
        </div>
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid="an-peak-days">
          <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 mb-3">Peak revenue days</h3>
          {peakDays.length === 0 && <p className="text-sm text-zinc-500">Not enough history yet.</p>}
          <ol className="space-y-2">
            {peakDays.map((d, i) => (
              <li key={d.period} className="flex items-center gap-3 py-1" data-testid={`an-peak-${i}`}>
                <span className="text-xs text-zinc-600 tabular-nums w-6">#{i + 1}</span>
                <span className="text-sm text-zinc-200 flex-1">{d.period}</span>
                <span className="text-sm text-zinc-300 tabular-nums">{money(d.revenue)}</span>
                <span className="text-xs text-zinc-500 tabular-nums w-16 text-right">{num(d.orders)} orders</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Day of week */}
      {dow.length > 0 && (
        <section data-testid="an-dow-section">
          <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 mb-4">Day-of-week performance</h2>
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4 h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dow.map((d) => ({ ...d, name: d.label.slice(0, 3) }))} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="name" stroke="#71717a" fontSize={11} />
                <YAxis stroke="#71717a" fontSize={11} tickFormatter={(v) => moneyShort(v)} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="revenue" fill="#f97316" radius={[4, 4, 0, 0]} name="Revenue" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {/* Marketplace performance */}
      {mpRows.length > 0 && (
        <section data-testid="an-marketplace-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 flex items-center gap-2">
              <Store className="h-3.5 w-3.5" /> Marketplace performance
            </h2>
            <span className="text-xs text-zinc-500">{marketplaces?.total_marketplaces || mpRows.length} tracked</span>
          </div>
          <div className="grid lg:grid-cols-5 gap-4">
            <div className="lg:col-span-3 rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4 h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={mpRows} margin={{ top: 10, right: 12, left: 0, bottom: 0 }} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" horizontal={false} />
                  <XAxis type="number" stroke="#71717a" fontSize={11} tickFormatter={(v) => moneyShort(v)} />
                  <YAxis dataKey="marketplace" type="category" stroke="#71717a" fontSize={11} width={100} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="revenue" fill="#f97316" radius={[0, 4, 4, 0]} name="Revenue" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="lg:col-span-2 rounded-2xl border border-zinc-800 bg-zinc-900/40 overflow-hidden">
              <table className="w-full">
                <thead className="bg-zinc-900/60 text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                  <tr>
                    <th className="px-3 py-2 text-left font-bold">Marketplace</th>
                    <th className="px-3 py-2 text-right font-bold">Contribution</th>
                    <th className="px-3 py-2 text-right font-bold">Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {mpRows.map((r) => {
                    const meta = TREND_META[r.trend] || TREND_META.unknown;
                    const Icon = meta.Icon;
                    return (
                      <tr key={r.marketplace} className="border-t border-zinc-800/60" data-testid={`an-mp-row-${r.marketplace}`}>
                        <td className="px-3 py-2 text-sm text-zinc-200">{r.marketplace}</td>
                        <td className="px-3 py-2 text-sm text-zinc-300 tabular-nums text-right">{r.contribution_pct.toFixed(1)}%</td>
                        <td className={`px-3 py-2 text-xs text-right ${meta.color}`}>
                          <Icon className="inline h-3 w-3 mr-1" />{pct(r.revenue_change_pct)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* Category performance */}
      {catRows.length > 0 && (
        <section data-testid="an-category-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 flex items-center gap-2">
              <Boxes className="h-3.5 w-3.5" /> Category performance
            </h2>
            <span className="text-xs text-zinc-500">{categories?.total_categories || catRows.length} tracked</span>
          </div>
          <div className="grid lg:grid-cols-5 gap-4">
            <div className="lg:col-span-3 rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4 h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={catRows} margin={{ top: 10, right: 12, left: 0, bottom: 0 }} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" horizontal={false} />
                  <XAxis type="number" stroke="#71717a" fontSize={11} tickFormatter={(v) => moneyShort(v)} />
                  <YAxis dataKey="category" type="category" stroke="#71717a" fontSize={11} width={130} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="revenue" fill="#f97316" radius={[0, 4, 4, 0]} name="Revenue" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="lg:col-span-2 rounded-2xl border border-zinc-800 bg-zinc-900/40 overflow-hidden">
              <table className="w-full">
                <thead className="bg-zinc-900/60 text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                  <tr>
                    <th className="px-3 py-2 text-left font-bold">Category</th>
                    <th className="px-3 py-2 text-right font-bold">Products</th>
                    <th className="px-3 py-2 text-right font-bold">Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {catRows.map((r) => {
                    const meta = TREND_META[r.trend] || TREND_META.unknown;
                    const Icon = meta.Icon;
                    return (
                      <tr key={r.category} className="border-t border-zinc-800/60" data-testid={`an-cat-row-${r.category}`}>
                        <td className="px-3 py-2 text-sm text-zinc-200 truncate max-w-[160px]">{r.category}</td>
                        <td className="px-3 py-2 text-sm text-zinc-300 tabular-nums text-right">{num(r.products)}</td>
                        <td className={`px-3 py-2 text-xs text-right ${meta.color}`}>
                          <Icon className="inline h-3 w-3 mr-1" />{pct(r.revenue_change_pct)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* Top products */}
      {topProducts.length > 0 && (
        <section data-testid="an-products-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 flex items-center gap-2">
              <Package className="h-3.5 w-3.5" /> Top product performance
            </h2>
            <span className="text-xs text-zinc-500">{products?.total_products || 0} products</span>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-zinc-800 bg-zinc-900/40">
            <table className="w-full">
              <thead className="bg-zinc-900/60 text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                <tr>
                  <th className="px-3 py-2 text-left font-bold">Product</th>
                  <th className="px-3 py-2 text-right font-bold">Revenue</th>
                  <th className="px-3 py-2 text-right font-bold hidden sm:table-cell">Units</th>
                  <th className="px-3 py-2 text-right font-bold hidden md:table-cell">Contribution</th>
                  <th className="px-3 py-2 text-right font-bold">Trend</th>
                  <th className="px-3 py-2 text-right font-bold">Health</th>
                </tr>
              </thead>
              <tbody>
                {topProducts.map((r) => {
                  const meta = TREND_META[r.trend] || TREND_META.unknown;
                  const Icon = meta.Icon;
                  return (
                    <tr key={r.product_id} className="border-t border-zinc-800/60 hover:bg-zinc-800/20" data-testid={`an-prod-${r.product_id}`}>
                      <td className="px-3 py-2 text-sm text-zinc-100 max-w-[280px] truncate">{r.name}</td>
                      <td className="px-3 py-2 text-sm text-zinc-100 tabular-nums text-right">{money(r.total_revenue)}</td>
                      <td className="px-3 py-2 text-sm text-zinc-300 tabular-nums text-right hidden sm:table-cell">{num(r.total_units)}</td>
                      <td className="px-3 py-2 text-sm text-zinc-300 tabular-nums text-right hidden md:table-cell">{r.contribution_pct.toFixed(1)}%</td>
                      <td className={`px-3 py-2 text-xs text-right ${meta.color}`}>
                        <Icon className="inline h-3 w-3 mr-1" />{pct(r.revenue_change_pct)}
                      </td>
                      <td className="px-3 py-2 text-xs text-right">
                        <span className={`px-2 py-0.5 rounded-md font-medium ${
                          r.health_bucket === "excellent" ? "bg-emerald-500/15 text-emerald-300" :
                          r.health_bucket === "healthy"   ? "bg-emerald-500/10 text-emerald-300/80" :
                          r.health_bucket === "needs_attention" ? "bg-amber-500/15 text-amber-300" :
                                                              "bg-red-500/15 text-red-300"
                        }`}>{r.health_score}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <footer className="pt-6 border-t border-zinc-900">
        <p className="text-xs text-zinc-600">
          Every number on this page is calculated by the Milkath BI Engine — the single source of truth. Raw orders are never queried from the frontend.
        </p>
      </footer>
    </div>
  );
}
